import React, { Component, ErrorInfo, ReactNode } from "react";
import { useSendBirdUnreadCount } from "@/src/hooks/useSendBirdUnreadCount";
import { useAppSelector } from "@/src/store";
import { useSendbird } from "@sendbird/uikit-react";
import { useEffect } from "react";
import { SendBirdErrorHandler } from "@/src/utils/sendbirdSDKUtils";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// Error Boundary Class Component
class SendBirdErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to our error handling service
    SendBirdErrorHandler.handleError(error, 'SendBirdUnreadCountProvider');
    // Error already logged by SendBirdErrorHandler
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI - render children without SendBird functionality
      return this.props.children;
    }

    return this.props.children;
  }
}

// Main Provider Component
const SendBirdUnreadCountProviderInner = ({ children }: { children: React.ReactNode }) => {
  const { state: { stores } } = useSendbird();
  const sb = stores?.sdkStore?.sdk;
  const { updateTotalUnreadCount, markChannelAsRead } = useSendBirdUnreadCount(sb);
  const showChannel = useAppSelector((state) => state.chat.showChannel);
  const currentChannel = useAppSelector((state) => state.chat.currentChannel);

  useEffect(() => {
    if (showChannel && sb && sb.currentUser) {
      // Wrap async operation with error handling
      void updateTotalUnreadCount().catch((error) => {
        SendBirdErrorHandler.handleError(error, 'updateTotalUnreadCount');
      });
    }
  }, [showChannel, updateTotalUnreadCount, sb]);

  useEffect(() => {
    if (currentChannel && sb && sb.currentUser) {
      // Wrap async operation with error handling
      void markChannelAsRead(currentChannel.url).catch((error) => {
        SendBirdErrorHandler.handleError(error, 'markChannelAsRead');
      });
    }
  }, [currentChannel, markChannelAsRead, sb]);

  return <>{children}</>;
};

// Export wrapped component with error boundary
const SendBirdUnreadCountProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <SendBirdErrorBoundary>
      <SendBirdUnreadCountProviderInner>
        {children}
      </SendBirdUnreadCountProviderInner>
    </SendBirdErrorBoundary>
  );
};

export default SendBirdUnreadCountProvider;
